import type { ProcedureAssignee, Procedure, ProcedureListStatus, ProcedureType } from '@/types/cgt'

import { API_BASE_URL as BASE } from '@/lib/apiBase'
import { authFetch, authHeaders, handleUnauthorized } from '@/lib/authFetch'

export interface ProcedurePage {
  items: Procedure[]
  total: number
}

/** One form-step's Assign To state, as returned by the assign endpoints. */
export interface StepAssignment {
  stepId: string
  assignee: ProcedureAssignee | null
}

export interface AssignProcedureResult {
  assignments: StepAssignment[]
  /**
   * Kit runs only: process-step executions already past ASSIGNED, which keep
   * whoever signed their start rather than following the new assignee. Zero for
   * every clinical procedure, where assignment does not reach the executions.
   */
  lockedSteps: number
}

function buildQs(params: Record<string, string | number | undefined>): string {
  const qs = new URLSearchParams()
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== '') qs.set(k, String(v))
  }
  const s = qs.toString()
  return s ? `?${s}` : ''
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await authFetch(`${BASE}${path}`, {
    headers: authHeaders(init?.headers),
    ...init,
  })
  if (!res.ok) {
    handleUnauthorized(res.status)
    const text = await res.text().catch(() => res.statusText)
    throw new Error(text || `API ${init?.method ?? 'GET'} ${path} failed (${res.status})`)
  }
  return res.json() as Promise<T>
}

/**
 * One unit a scheduled run would hold. Rows that CANNOT be booked are included
 * with a reason rather than omitted — a shorter list would read as a shorter
 * run, when what happened is that a step could not be staffed.
 */
export interface PlannedReservation {
  submissionId: string
  fieldId: string
  formName: string
  requirementId: string
  equipmentTypeName: string
  equipmentId: string | null
  equipmentLabel: string | null
  startsAt: string | null
  endsAt: string | null
  /**
   * `already_held` means held for EXACTLY this window, or already started at
   * the bench — not merely that some hold exists. A hold taken for a time the
   * run has moved away from leaves the row bookable and is named in
   * `supersedesReservationId`.
   */
  blockedReason: 'no_unit_free' | 'no_run_time' | 'already_held' | null
  busyUntil: string | null
  /** A live hold this row would replace, taken for a window this run has left. */
  supersedesReservationId: string | null
}

/**
 * What a step will consume, and what confirming the plan will HOLD.
 *
 * A hold does not move stock: the lot keeps its quantity and the material stays
 * on the shelf. What changes is availability — the inventory page's Reserved
 * and Available columns, FEFO and pick lists all stop offering it to anything
 * else. The deduction still happens at the signed Start/End.
 */
export interface PlannedConsumption {
  submissionId: string
  fieldId: string
  formName: string
  requirementId: string
  itemLabel: string
  qtyRequired: number
  uom: string | null
  /**
   * Already held, so confirming will not take it again. Resolved per STEP —
   * the backend refuses a step outright when any of its lines is held.
   */
  alreadyHeld: boolean
  lots: Array<{ lot: string; qty: number }>
  shortfall: number
  isShort: boolean
}

export interface ReservationPlan {
  startsAt: string
  rows: PlannedReservation[]
  /** How many rows would actually be booked. */
  bookable: number
  /** Materials the run will consume, and that confirming will hold. */
  consumption: PlannedConsumption[]
  /** How many material lines stock cannot cover — these are NOT held. */
  shortLines: number
  /**
   * Material lines confirming would actually hold. The inventory half's answer
   * to `bookable`, and the confirm button gates on BOTH — a run whose machines
   * are all held but whose material is not still has something to do.
   */
  holdableLines: number
}

/** What holding one step's material actually did. */
export interface MaterialHoldOutcome {
  submissionId: string
  fieldId: string
  formName: string
  /** Requirement lines now held for this step. */
  reserved: number
  /** Lines skipped because stock could not cover them. */
  short: number
  /** Non-null only when the hold failed outright. */
  message: string | null
}

/**
 * What a run currently holds, for warning about a reschedule before it happens.
 *
 * `startedUnits` is a SUBSET of `equipmentUnits` and is the part a move will not
 * touch — a hold an operator has verified at the bench stays where it is.
 */
export interface ProcedureHolds {
  equipmentUnits: number
  startedUnits: number
  materialLines: number
}

export interface ScheduleReservationResult {
  reserved: PlannedReservation[]
  refused: Array<PlannedReservation & { message: string }>
  /** Per step, omitting steps that had no material to hold. */
  materials: MaterialHoldOutcome[]
}

/**
 * A run a CAPA can be raised on, as `GET /procedures/capa-targets` returns it:
 * enough to LABEL the run without a second lookup.
 */
export interface CapaTarget {
  id: string
  patientMrn: string | null
  workOrderNumber: string | null
  mfgNumber: string | null
  procedureTemplate: { name: string; type?: ProcedureType | null }
}

export const proceduresApi = {
  /**
   * The in-progress runs a CAPA can be raised on — what the Create CAPA picker
   * offers. In-progress because that is what the create accepts: attaching a
   * form to a finished run is refused server-side.
   */
  listCapaTargets: (): Promise<CapaTarget[]> =>
    request<CapaTarget[]>('/procedures/capa-targets'),

  /**
   * The distinct patient MRNs among the procedures `listAll` would return for
   * the same page filters (z8nrz7e8kw): feeds the "filter by patient" dropdown
   * without loading every procedure in the tenant.
   */
  patientMrns: (params: { kitOnly?: boolean; type?: ProcedureType; capaOnly?: boolean } = {}): Promise<string[]> =>
    request<string[]>(
      `/procedures/patient-mrns${buildQs({
        isTestData: 'false',
        ...(params.kitOnly ? { kitOnly: 'true' } : {}),
        ...(params.type ? { type: params.type } : {}),
        ...(params.capaOnly ? { capaOnly: 'true' } : {}),
      })}`,
    ),

  /**
   * `patientMrns` with each patient's display name (z8nrz7e8kw), so the "filter
   * by patient" dropdown can label its entries without the page downloading
   * every submission to name them. 404 on a backend that predates it.
   */
  patientOptions: (
    params: { kitOnly?: boolean; type?: ProcedureType; capaOnly?: boolean } = {},
  ): Promise<{ mrn: string; name: string | null }[]> =>
    request<{ mrn: string; name: string | null }[]>(
      `/procedures/patient-options${buildQs({
        isTestData: 'false',
        ...(params.kitOnly ? { kitOnly: 'true' } : {}),
        ...(params.type ? { type: params.type } : {}),
        ...(params.capaOnly ? { capaOnly: 'true' } : {}),
      })}`,
    ),

  // Patient flows use real backend procedure rows (not scheduling mocks).
  // `progress: 'run'` attaches each row's step progress, counted for that run
  // alone as its journey would (z8nrz7fzk7).
  listForPatient: (patientMrn: string, params: { progress?: 'run' } = {}): Promise<ProcedurePage> =>
    request<ProcedurePage>(`/procedures${buildQs({ patientMrn, isTestData: 'false', progress: params.progress })}`),

  // All procedures — optionally filtered and paginated.
  listAll: (
    params: {
      q?: string
      patientMrn?: string
      skip?: number
      take?: number
      /** Return ONLY kit runs — what the Kits page lists. Excluded by default. */
      kitOnly?: boolean
      /**
       * Return only procedures whose TEMPLATE carries this workflow type — what
       * the MES page lists. Independent of `kitOnly`, which resolves kit runs by
       * form category rather than by type (z8nrz7ckj9).
       */
      type?: ProcedureType
      /**
       * Return ONLY CAPA procedures — a template typed CAPA, or any procedure
       * holding a form of category `capa` (sequenced or added ad hoc). What the
       * CAPA page lists.
       *
       * Unlike `kitOnly` and `type`, this takes nothing away from the default
       * list: CAPA is a lens over patient care, so a transplant carrying a CAPA
       * form shows on both pages.
       */
      capaOnly?: boolean
      /**
       * Inclusive `YYYY-MM-DD` bounds on when the procedure was RAISED
       * (createdAt). Filtered server-side: the list is paged, so narrowing a
       * page would hide matches on other pages and misreport the total.
       */
      createdFrom?: string
      createdTo?: string
      /** Matches a procedure with ANY step assigned to this user. */
      assigneeId?: string
      /** true: some step is assigned; false: none is (z8nrz7f9u5). `assigneeId` wins. */
      assigned?: boolean
      /** Only runs made for this customer — Kit and MES runs carry one (z8nrz7e714). */
      customerId?: string
      /** Includes `pending_review`, which the server derives from the run's forms. */
      status?: ProcedureListStatus
      /**
       * Procedures of ANY of these types. Replaces the default exclusions of
       * kit runs, MES and Inventory, and takes precedence over `type`. What the
       * Overall Status board lists.
       */
      types?: ProcedureType[]
      /** Attach `summary` to every item (progress, holds, CAPA, P/H). */
      summary?: boolean
      /** Attach `progress` to every item: the tables' step counts (z8nrz7e8kw). */
      progress?: boolean
      /** Attach `patientName` to every item (z8nrz7e8kw). */
      patientNames?: boolean
      /** `column:asc|desc`, applied server-side before paging (z8nrz7e4h6). */
      sort?: string
    } = {},
  ): Promise<ProcedurePage> =>
    request<ProcedurePage>(
      `/procedures${buildQs({
        isTestData: 'false',
        q: params.q,
        patientMrn: params.patientMrn,
        skip: params.skip,
        take: params.take,
        ...(params.kitOnly ? { kitOnly: 'true' } : {}),
        ...(params.type ? { type: params.type } : {}),
        ...(params.capaOnly ? { capaOnly: 'true' } : {}),
        createdFrom: params.createdFrom,
        createdTo: params.createdTo,
        assigneeId: params.assigneeId,
        ...(params.assigned !== undefined ? { assigned: String(params.assigned) } : {}),
        customerId: params.customerId,
        status: params.status,
        types: params.types?.length ? params.types.join(',') : undefined,
        ...(params.summary ? { summary: 'true' } : {}),
        ...(params.progress ? { progress: 'true' } : {}),
        ...(params.patientNames ? { patientNames: 'true' } : {}),
        sort: params.sort,
      })}`,
    ),

  get: (id: string): Promise<Procedure> => request<Procedure>(`/procedures/${id}`),

  create: (payload: {
    /**
     * Omit for a type that has no patient at creation — an MES order takes its
     * patient from an Epic order, or never has one (z8nrz7ckj9). Every other
     * type still requires one, enforced server-side where the template's type is
     * known. Send it or omit it; never send an empty string, which the server
     * reads as a supplied value and rejects as empty. No SHAPE is required of
     * it — a real Epic MRN is a bare `1234567` (z8nrz7dyc2).
     */
    patientMrn?: string
    procedureTemplateId: string
    createdBy?: string
    scheduledDate?: string
    dueDate?: string
    /**
     * Days after the scheduled start this run is due, overriding the template's
     * default. OMIT to use the template — do not send null, and note 0 is a real
     * value meaning due the same day. Ignored when `dueDate` is supplied, which
     * is an explicit override.
     */
    dueOffsetDays?: number
    /** Units produced; multiplies inventory requirements. Defaults to 1. */
    quantity?: number
    /**
     * Omit to have one minted (`WO-<yy>-<nnnn>`, Transplant only); send one to
     * carry a number issued outside this system. A duplicate is a 409.
     */
    workOrderNumber?: string
    /** MES only here (kits start through pickListsApi). Omit for none. */
    customerId?: string
    /** The Product Code the MES run makes (z8nrz7fvhx). MES only; omit for none. */
    mesProductId?: string
  }): Promise<Procedure> =>
    request<Procedure>('/procedures', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  // Procedure-level planning dates (YYYY-MM-DD), work order and quantity.
  // Tri-state per field: omitted = leave untouched, explicit null = clear.
  /**
   * What this procedure would reserve from a scheduled start. Read-only.
   *
   * Server-side because it spans every form in the run: doing it here would be
   * one round trip per process field, and the unit-picking rule would have to
   * be duplicated away from the type gate that guards it.
   */
  reservationPlan: (id: string, startsAt: string) =>
    request<ReservationPlan>(
      `/procedures/${id}/reservation-plan?startsAt=${encodeURIComponent(startsAt)}`,
    ),

  /**
   * What this run holds right now — the counts the reschedule confirm needs.
   *
   * Not `reservationPlan`: that needs a proposed start and runs a full FEFO
   * proposal per step. This answers only "what is about to be re-timed".
   */
  holds: (id: string) => request<ProcedureHolds>(`/procedures/${id}/holds`),

  /**
   * Book the run's equipment. PARTIAL: a busy unit blocks its own row only,
   * unlike the single-step reserve and retime, which are atomic.
   */
  reserveForSchedule: (id: string, startsAt: string) =>
    request<ScheduleReservationResult>(`/procedures/${id}/reservations`, {
      method: 'POST',
      body: JSON.stringify({ startsAt }),
    }),

  // Two 409s to expect: a work order that duplicates another procedure's, and a
  // quantity change on a run that has already allocated inventory.
  update: (
    id: string,
    payload: {
      scheduledDate?: string | null
      dueDate?: string | null
      workOrderNumber?: string | null
      /**
       * Not nullable — a procedure always produces some number of units. The
       * server refuses the change once material has been allocated (409).
       */
      quantity?: number
    },
  ): Promise<Procedure> =>
    request<Procedure>(`/procedures/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    }),

  // Cancels the procedure (z8nrz7f7g0): it is closed as cancelled and its open
  // forms are voided. Nothing is deleted — every form and audit trail is kept.
  remove: (id: string, reason?: string): Promise<{ ok: true }> =>
    request<{ ok: true }>(`/procedures/${id}`, {
      method: 'DELETE',
      body: JSON.stringify(reason ? { reason } : {}),
    }),

  // Roster for the Assign To pickers. Requires procedures.assign server-side —
  // don't call it for users who can't assign (the failure is a 403).
  //
  // With `procedureId`, the server narrows it to the people who can perform
  // that run's procedure. A procedure nobody has been granted returns the full
  // roster, so this stays correct before the matrix is populated.
  listAssignableUsers: (procedureId?: string): Promise<ProcedureAssignee[]> =>
    request<ProcedureAssignee[]>(
      procedureId
        ? `/procedures/assignable-users?procedureId=${encodeURIComponent(procedureId)}`
        : '/procedures/assignable-users',
    ),

  // Procedure-level cascade: assigns (or, with null, clears) EVERY form of the procedure.
  assignProcedure: (id: string, assigneeId: string | null): Promise<AssignProcedureResult> =>
    request<AssignProcedureResult>(`/procedures/${id}/assignee`, {
      method: 'PATCH',
      body: JSON.stringify({ assigneeId }),
    }),

  // Assign To for one form step of the procedure (null unassigns).
  assignStep: (
    id: string,
    stepId: string,
    assigneeId: string | null,
  ): Promise<StepAssignment & { lockedSteps: number }> =>
    request<StepAssignment & { lockedSteps: number }>(`/procedures/${id}/steps/${stepId}/assignee`, {
      method: 'PATCH',
      body: JSON.stringify({ assigneeId }),
    }),

  /**
   * How many times one step runs in THIS procedure — the per-run override of
   * the template's default (z8nrz7dkkc).
   *
   * `surplusSubmissions` reports performances that already exist above the new
   * number. Lowering a count never deletes them (a signed performance is not
   * discarded to tidy a number), so the caller should say so rather than let
   * them vanish from the rail unexplained.
   */
  setStepRepeats: (
    id: string,
    stepId: string,
    repeatCount: number,
  ): Promise<{ stepId: string; repeatCount: number; surplusSubmissions: number }> =>
    request(`/procedures/${id}/steps/${stepId}/repeats`, {
      method: 'PATCH',
      body: JSON.stringify({ repeatCount }),
    }),

  /**
   * Assign ONE repeat of a repeating step; null unassigns.
   *
   * Unassigning does not blank the repeat — it drops back to whoever holds the
   * step as a whole, which is the same thing the journey resolves.
   */
  assignStepRepeat: (
    id: string,
    stepId: string,
    repeatIndex: number,
    assigneeId: string | null,
  ): Promise<{ stepId: string; repeatIndex: number; assignee: ProcedureAssignee | null }> =>
    request(`/procedures/${id}/steps/${stepId}/repeats/${repeatIndex}/assignee`, {
      method: 'PATCH',
      body: JSON.stringify({ assigneeId }),
    }),
}
